import { Component, OnDestroy, OnInit } from '@angular/core';
import { FormBuilder, FormGroup } from '@angular/forms';
import { ServerResponse } from 'src/app/shared/models/dto/server-response.dto';
import { NzTableQueryParams } from 'ng-zorro-antd/table';
import { forkJoin, merge, of, Subject } from 'rxjs';
import {
  catchError,
  debounceTime,
  distinctUntilChanged,
  map,
  switchMap,
  takeUntil,
  tap,
} from 'rxjs/operators';
import { StpActivityLogStorageService } from 'src/app/modules/stp-operations/services/stp-activity-log-storage.service';
import { StpRegistrationActivityLog } from '../../models/stp-registration-activity-log.model';
import { StpRegistrationActivityLogSearchCriteria } from '../../models/stp-registration-activity-log-search-criteria.model';
import { UserStorageService } from 'src/app/modules/user/services/user-storage.service';

type DetailMode = 'INSERT' | 'UPDATE' | 'DELETE';

interface FormattedValue {
  text: string;
  // nested objects/arrays are pretty-printed and rendered in a <pre>
  isBlock: boolean;
  // set on changed rows: text split into unchanged/changed parts
  segments?: ValueSegment[];
}

interface ValueSegment {
  text: string;
  changed: boolean;
}

interface FieldRow {
  label: string;
  previous: FormattedValue;
  current: FormattedValue;
  changed: boolean;
  // set for createdBy/updatedBy rows, resolved to the user's email
  previousUserId?: number;
  currentUserId?: number;
}

interface DataSection {
  title: string;
  rows: FieldRow[];
  changedCount: number;
  // set when the stored value is not valid JSON
  rawText?: string;
}

interface ParsedData {
  value: any;
  invalid: boolean;
}

const EMPTY = '—';

const USER_ID_FIELDS = ['createdBy', 'updatedBy', 'created_by', 'updated_by'];

@Component({
  selector: 'app-stp-activity-log',
  templateUrl: './stp-activity-log.component.html',
  styleUrls: ['./stp-activity-log.component.scss'],
})
export class StpActivityLogComponent implements OnInit, OnDestroy {
  searchForm: FormGroup;
  private destroy$ = new Subject<void>();
  activityLogSearchCriteria = new StpRegistrationActivityLogSearchCriteria();
  dateRange: [Date, Date] | null = null;
  activityLogList: StpRegistrationActivityLog[] = [];

  actionTypes: { id: string; name: string }[] = [
    { id: 'CREATE', name: 'CREATE' },
    { id: 'INSERT', name: 'INSERT' },
    { id: 'UPDATE', name: 'UPDATE' },
    { id: 'DELETE', name: 'DELETE' },
  ];

  actionTypeColors: { [actionType: string]: string } = {
    INSERT: 'green',
    UPDATE: 'blue',
    DELETE: 'red',
  };

  // '' = default (grey) tag
  readonly applicationStatusColors: { [status: string]: string } = {
    'New Application On Going': '',
    'Application Submitted': 'blue',
    'Resubmitted After Correction': 'geekblue',
    'Temporary Accepted': 'cyan',
    'New Application Rollbacked': 'volcano',
    'Temporarily Rejected': 'red',
    Approved: 'green',
    'Draft Deleted': '',
    Renew: 'purple',
    'Renew Application Submitted': 'purple',
    'Re-Application': 'orange',
    'Re-Application Submitted': 'orange',
  };
  readonly applicationStatuses = Object.keys(this.applicationStatusColors);
  readonly temporarilyRejected = 'Temporarily Rejected';

  // who made the change (worked out by the backend from the acting user)
  readonly actorTypeColors: { [actorType: string]: string } = {
    'NSDA User': 'magenta',
    'STP User': 'blue',
    System: '',
  };
  readonly actorTypes = Object.keys(this.actorTypeColors);

  // suggestions for the free-text Table Name filter
  readonly tableNameOptions: { value: string; label: string }[] = [
    { value: 'stp_main_dump', label: 'Renew / Re-application' },
  ];

  // temporary rejection reason modal
  isReasonVisible = false;
  reasonText = '';

  total: number = 0;
  size: number = 10;
  page: number = 1;
  sortingKey: string = 'action_time';
  sortingValue: string = 'descend';

  // STP Name filter (server-side search)
  stpOptions: { id: number; stpName: string }[] = [];
  isStpLoading = false;
  private stpSearch$ = new Subject<string>();
  private stpReload$ = new Subject<string>();

  // details modal
  isDetailVisible = false;
  selectedLog: StpRegistrationActivityLog | null = null;
  detailMode: DetailMode = 'INSERT';
  isBulk = false;
  dataSections: DataSection[] = [];

  // inline so they apply inside the modal overlay regardless of style scoping
  readonly changeStyles: { [key: string]: { [style: string]: string } } = {
    field: {
      'background-color': '#fff3b0',
      'border-left': '4px solid #faad14',
      'font-weight': '700',
    },
    previousCell: { 'background-color': '#fff1f0', color: '#a8071a' },
    newCell: {
      'background-color': '#f6ffed',
      color: '#237804',
      'font-weight': '600',
    },
    'diff-removed': {
      'background-color': '#ffa39e',
      color: '#820014',
      'text-decoration': 'line-through',
      'border-radius': '2px',
      padding: '0 2px',
    },
    'diff-added': {
      'background-color': '#95de64',
      color: '#135200',
      'font-weight': '700',
      'border-radius': '2px',
      padding: '0 2px',
    },
  };
  private userEmailCache = new Map<number, string>();

  constructor(
    private stpActivityLogStorageService: StpActivityLogStorageService,
    private userStorageService: UserStorageService,
    private fb: FormBuilder
  ) {}

  formatDate(date: Date): string {
    const year = date.getFullYear();
    const month = ('0' + (date.getMonth() + 1)).slice(-2);
    const day = ('0' + date.getDate()).slice(-2);
    return `${year}-${month}-${day}`; // backend expects yyyy-MM-dd
  }

  onDateRangeChange(): void {
    const range = this.searchForm.get('dateRange')?.value;
    if (range && range[0] && range[1]) {
      this.activityLogSearchCriteria.startDate = this.formatDate(range[0]); // e.g. 2025-09-17
      this.activityLogSearchCriteria.endDate = this.formatDate(range[1]);
    } else {
      this.activityLogSearchCriteria.startDate = '';
      this.activityLogSearchCriteria.endDate = '';
    }
  }

  ngOnInit(): void {
    this.createSearchForm();
    this.initStpSearch();
    this.stpReload$.next('');
    this.submitSearchForm();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  createSearchForm() {
    this.searchForm = this.fb.group({
      stpName: [null],
      applicationStatus: [null],
      actorType: [null],
      tableName: [null],
      description: [null],
      userName: [null],
      actionType: [null],
      dateRange: [[]],
    });
  }

  submitSearchForm(): void {
    this.activityLogSearchCriteria = this.mapFormToCriteria();
    this.loadTableData(this.activityLogSearchCriteria);
  }

  mapFormToCriteria(): StpRegistrationActivityLogSearchCriteria {
    const values = this.searchForm.value;
    const criteria = new StpRegistrationActivityLogSearchCriteria();

    criteria.stpName = values.stpName || null;
    criteria.applicationStatus = values.applicationStatus || null;
    criteria.actorType = values.actorType || null;
    criteria.userName = values.userName;
    criteria.actionType = values.actionType;
    criteria.tableName = values.tableName;
    criteria.description = values.description;

    if (values.dateRange?.[0] && values.dateRange?.[1]) {
      criteria.startDate = this.formatDate(values.dateRange[0]);
      criteria.endDate = this.formatDate(values.dateRange[1]);
    } else {
      criteria.startDate = '';
      criteria.endDate = '';
    }

    criteria.pageNumber = this.page - 1;
    criteria.pageSize = this.size;
    criteria.sortingKey = this.sortingKey;
    criteria.sortingValue = this.sortingValue;

    return criteria;
  }

  loadTableData(
    activityLogSearchCriteria: StpRegistrationActivityLogSearchCriteria
  ): void {
    this.stpActivityLogStorageService
      .readSTPActivityLog(activityLogSearchCriteria)
      .subscribe({
        next: (res: ServerResponse) => {
          this.activityLogList = res?.data?.content ?? [];
          this.total = Number(res?.data?.totalElements) || 0;
        },
        error: () => {
          this.activityLogList = [];
          this.total = 0;
        },
      });
  }

  onQueryParamsChange(params: NzTableQueryParams): void {
    this.page = params.pageIndex;
    this.size = params.pageSize;
    // no column sorted (e.g. sort cleared) -> default to latest action first
    const activeSort = params.sort.find((element) => element.value != null);
    this.sortingKey = activeSort ? activeSort.key : 'action_time';
    this.sortingValue = activeSort ? (activeSort.value as string) : 'descend';

    this.activityLogSearchCriteria.pageSize = this.size;
    this.activityLogSearchCriteria.pageNumber = this.page - 1;
    this.activityLogSearchCriteria.sortingKey = this.sortingKey;
    this.activityLogSearchCriteria.sortingValue = this.sortingValue;
    this.submitSearchForm();
  }

  onRefresh(): void {
    this.searchForm.reset();
    this.dateRange = null;
    this.page = 1;
    this.sortingKey = 'action_time';
    this.sortingValue = 'descend';

    this.activityLogSearchCriteria = new StpRegistrationActivityLogSearchCriteria();
    this.activityLogSearchCriteria.pageNumber = 0;
    this.activityLogSearchCriteria.pageSize = this.size;
    this.activityLogSearchCriteria.sortingKey = this.sortingKey;
    this.activityLogSearchCriteria.sortingValue = this.sortingValue;

    this.loadTableData(this.activityLogSearchCriteria);
    this.stpReload$.next('');
  }

  // ---------------------------------------------------------------------------
  // STP Name filter
  // ---------------------------------------------------------------------------

  hasRejectionReason(log: StpRegistrationActivityLog): boolean {
    return (
      log.applicationStatus === this.temporarilyRejected &&
      !!log.remarks &&
      log.remarks.trim().length > 0
    );
  }

  openReason(log: StpRegistrationActivityLog): void {
    this.reasonText = log.remarks || '';
    this.isReasonVisible = true;
  }

  closeReason(): void {
    this.isReasonVisible = false;
  }

  onStpSearch(value: string): void {
    this.stpSearch$.next(value);
  }

  private initStpSearch(): void {
    // typed text is debounced; initial load/reset (stpReload$) fire immediately
    merge(this.stpSearch$.pipe(debounceTime(350)), this.stpReload$)
      .pipe(
        map((value) => (value || '').trim()),
        distinctUntilChanged(),
        tap(() => (this.isStpLoading = true)),
        switchMap((stpName) =>
          this.stpActivityLogStorageService
            .getStpListForActivityLog(stpName)
            .pipe(catchError(() => of(null)))
        ),
        takeUntil(this.destroy$)
      )
      .subscribe((res: ServerResponse | null) => {
        this.stpOptions =
          res?.success && Array.isArray(res.data) ? res.data : [];
        this.isStpLoading = false;
      });
  }

  // ---------------------------------------------------------------------------
  // Details modal
  // ---------------------------------------------------------------------------

  openDetails(log: StpRegistrationActivityLog): void {
    const previous = this.parseLogData(log.previousData);
    const current = this.parseLogData(log.newData);

    this.selectedLog = log;
    this.detailMode = this.resolveDetailMode(log.actionType, previous, current);
    this.isBulk =
      Array.isArray(previous.value) || Array.isArray(current.value);
    this.dataSections = this.isBulk
      ? this.buildBulkSections(previous, current)
      : [this.buildSection('', previous, current)];
    this.isDetailVisible = true;
    this.resolveUserEmails(this.dataSections);
  }

  // createdBy/updatedBy hold user ids; show the user's email instead
  private resolveUserEmails(sections: DataSection[]): void {
    const rows = sections.reduce(
      (all, section) => all.concat(section.rows),
      [] as FieldRow[]
    );
    const userRows = rows.filter(
      (row) => row.previousUserId != null || row.currentUserId != null
    );
    if (!userRows.length) return;

    const applyEmails = () =>
      userRows.forEach((row) => {
        if (row.previousUserId != null) {
          row.previous = this.userValue(row.previousUserId);
        }
        if (row.currentUserId != null) {
          row.current = this.userValue(row.currentUserId);
        }
        this.markChangedParts(row);
      });

    applyEmails();

    const pendingIds = Array.from(
      new Set(
        userRows
          .reduce(
            (ids, row) => ids.concat([row.previousUserId, row.currentUserId]),
            [] as (number | undefined)[]
          )
          .filter(
            (id): id is number => id != null && !this.userEmailCache.has(id)
          )
      )
    );
    if (!pendingIds.length) return;

    forkJoin(
      pendingIds.map((id) =>
        this.userStorageService.readUserById(id).pipe(
          map((res: ServerResponse) => res?.data?.email || null),
          catchError(() => of(null)),
          tap((email) => {
            if (email) this.userEmailCache.set(id, email);
          })
        )
      )
    )
      .pipe(takeUntil(this.destroy$))
      .subscribe(() => applyEmails());
  }

  private userValue(userId: number): FormattedValue {
    // falls back to the id when the user could not be loaded
    return {
      text: this.userEmailCache.get(userId) ?? String(userId),
      isBlock: false,
    };
  }

  private toUserId(value: any): number | undefined {
    if (value === null || value === undefined || value === '') return undefined;
    const id = Number(value);
    return Number.isInteger(id) && id > 0 ? id : undefined;
  }

  closeDetails(): void {
    this.isDetailVisible = false;
    this.selectedLog = null;
    this.dataSections = [];
  }

  private resolveDetailMode(
    actionType: string,
    previous: ParsedData,
    current: ParsedData
  ): DetailMode {
    switch ((actionType || '').toUpperCase()) {
      case 'UPDATE':
        return 'UPDATE';
      case 'DELETE':
        return 'DELETE';
      case 'INSERT':
      case 'CREATE':
        return 'INSERT';
    }
    if (previous.value != null && current.value != null) return 'UPDATE';
    return previous.value != null ? 'DELETE' : 'INSERT';
  }

  private parseLogData(raw: any): ParsedData {
    if (raw === null || raw === undefined || raw === '') {
      return { value: null, invalid: false };
    }
    if (typeof raw !== 'string') return { value: raw, invalid: false };
    try {
      return { value: JSON.parse(raw), invalid: false };
    } catch {
      return { value: raw, invalid: true };
    }
  }

  private buildBulkSections(
    previous: ParsedData,
    current: ParsedData
  ): DataSection[] {
    const toList = (data: ParsedData): any[] => {
      if (data.value == null) return [];
      return Array.isArray(data.value) ? data.value : [data.value];
    };
    const prevList = this.detailMode === 'INSERT' ? [] : toList(previous);
    const currList = this.detailMode === 'DELETE' ? [] : toList(current);
    const count = Math.max(prevList.length, currList.length);

    const sections: DataSection[] = [];
    for (let i = 0; i < count; i++) {
      const prevItem = prevList[i] ?? null;
      const currItem = currList[i] ?? null;
      const id = (currItem ?? prevItem)?.id;
      sections.push(
        this.buildSection(
          `Item ${i + 1}${id != null ? ' (ID: ' + id + ')' : ''}`,
          { value: prevItem, invalid: false },
          { value: currItem, invalid: false }
        )
      );
    }
    return sections;
  }

  private buildSection(
    title: string,
    previous: ParsedData,
    current: ParsedData
  ): DataSection {
    const shown = this.detailMode === 'DELETE' ? previous : current;
    if (this.detailMode !== 'UPDATE' && shown.invalid) {
      return { title, rows: [], changedCount: 0, rawText: shown.value };
    }
    if (this.detailMode === 'UPDATE' && (previous.invalid || current.invalid)) {
      // fall back to a single raw comparison row
      previous = { value: previous.value, invalid: false };
      current = { value: current.value, invalid: false };
    }

    const prevValue = this.detailMode === 'INSERT' ? null : previous.value;
    const currValue = this.detailMode === 'DELETE' ? null : current.value;

    let rows: FieldRow[];
    if (this.isPlainObjectOrNull(prevValue) && this.isPlainObjectOrNull(currValue)) {
      const keys = Array.from(
        new Set([
          ...Object.keys(prevValue || {}),
          ...Object.keys(currValue || {}),
        ])
      ).sort();
      rows = keys.map((key) => {
        const row = this.buildRow(
          this.formatKey(key),
          prevValue?.[key],
          currValue?.[key]
        );
        if (USER_ID_FIELDS.includes(key)) {
          row.previousUserId = this.toUserId(prevValue?.[key]);
          row.currentUserId = this.toUserId(currValue?.[key]);
        }
        return row;
      });
    } else {
      rows = [this.buildRow('Value', prevValue, currValue)];
    }

    return {
      title,
      rows,
      changedCount:
        this.detailMode === 'UPDATE' ? rows.filter((r) => r.changed).length : 0,
    };
  }

  private buildRow(label: string, prevValue: any, currValue: any): FieldRow {
    const row: FieldRow = {
      label,
      previous: this.formatValue(prevValue),
      current: this.formatValue(currValue),
      changed:
        this.detailMode === 'UPDATE' &&
        this.canonical(prevValue) !== this.canonical(currValue),
    };
    this.markChangedParts(row);
    return row;
  }

  // splits previous/current text so the parts that differ can be coloured
  private markChangedParts(row: FieldRow): void {
    if (!row.changed) return;
    const prev = row.previous;
    const curr = row.current;

    // value added or removed: the whole other side is the change
    if (prev.text === EMPTY || curr.text === EMPTY) {
      if (prev.text !== EMPTY) prev.segments = [{ text: prev.text, changed: true }];
      if (curr.text !== EMPTY) curr.segments = [{ text: curr.text, changed: true }];
      return;
    }

    if (prev.isBlock || curr.isBlock) {
      // pretty-printed JSON: mark lines that do not appear on the other side
      const prevLines = prev.text.split('\n');
      const currLines = curr.text.split('\n');
      const toSegments = (lines: string[], other: string[]): ValueSegment[] => {
        const remaining = other.slice();
        return lines.map((line, i) => {
          const idx = remaining.indexOf(line);
          if (idx >= 0) remaining.splice(idx, 1);
          return {
            text: line + (i < lines.length - 1 ? '\n' : ''),
            changed: idx < 0,
          };
        });
      };
      prev.segments = toSegments(prevLines, currLines);
      curr.segments = toSegments(currLines, prevLines);
      return;
    }

    // plain text: keep the common prefix/suffix, mark the middle
    const a = prev.text;
    const b = curr.text;
    let start = 0;
    while (start < a.length && start < b.length && a[start] === b[start]) {
      start++;
    }
    let endA = a.length;
    let endB = b.length;
    while (endA > start && endB > start && a[endA - 1] === b[endB - 1]) {
      endA--;
      endB--;
    }
    const split = (text: string, end: number): ValueSegment[] =>
      [
        { text: text.slice(0, start), changed: false },
        { text: text.slice(start, end), changed: true },
        { text: text.slice(end), changed: false },
      ].filter((s) => s.text.length > 0);
    prev.segments = split(a, endA);
    curr.segments = split(b, endB);
  }

  private isPlainObjectOrNull(value: any): boolean {
    return (
      value === null ||
      value === undefined ||
      (typeof value === 'object' && !Array.isArray(value))
    );
  }

  private formatKey(key: string): string {
    return key
      .replace(/_/g, ' ')
      .replace(/([a-z])([A-Z])/g, '$1 $2')
      .replace(/^./, (s) => s.toUpperCase());
  }

  private formatValue(value: any): FormattedValue {
    if (value === null || value === undefined || value === '') {
      return { text: EMPTY, isBlock: false };
    }
    if (value === true || value === 'true') return { text: 'Yes', isBlock: false };
    if (value === false || value === 'false') return { text: 'No', isBlock: false };

    if (typeof value === 'object') {
      return { text: JSON.stringify(value, null, 2), isBlock: true };
    }

    if (typeof value === 'string') {
      // nested JSON stored as a string
      const trimmed = value.trim();
      if (/^[\[{]/.test(trimmed)) {
        try {
          return {
            text: JSON.stringify(JSON.parse(trimmed), null, 2),
            isBlock: true,
          };
        } catch {
          // not JSON, show as-is
        }
      }
      const dateTime = this.formatDateTime(value);
      if (dateTime) return { text: dateTime, isBlock: false };
    }

    return { text: String(value), isBlock: false };
  }

  // ISO date-times: values with a zone (Z / +00:00, as Jackson writes them)
  // are converted to the browser's local time; zone-less values are shown as-is
  private formatDateTime(value: string): string | null {
    const m = value.match(
      /^(\d{4}-\d{2}-\d{2})T(\d{2}:\d{2}:\d{2})(\.\d+)?(Z|[+-]\d{2}:?\d{2})?$/
    );
    if (!m) return null;
    if (!m[4]) return `${m[1]} ${m[2]}`;

    const date = new Date(value);
    if (isNaN(date.getTime())) return `${m[1]} ${m[2]}`;
    const pad = (n: number) => ('0' + n).slice(-2);
    return (
      `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())} ` +
      `${pad(date.getHours())}:${pad(date.getMinutes())}:${pad(date.getSeconds())}`
    );
  }

  // stable representation for change detection (ignores key order, '' == null)
  private canonical(value: any): string {
    if (value === undefined || value === '') value = null;
    if (typeof value === 'string') {
      const trimmed = value.trim();
      if (/^[\[{]/.test(trimmed)) {
        try {
          value = JSON.parse(trimmed);
        } catch {
          return trimmed;
        }
      } else {
        return trimmed;
      }
    }
    if (Array.isArray(value)) {
      return '[' + value.map((v) => this.canonical(v)).join(',') + ']';
    }
    if (value !== null && typeof value === 'object') {
      return (
        '{' +
        Object.keys(value)
          .sort()
          .map((k) => JSON.stringify(k) + ':' + this.canonical(value[k]))
          .join(',') +
        '}'
      );
    }
    return String(value);
  }
}
